import { useModal } from 'mui-modal-provider'
import { useCallback, useMemo } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { EFTypography } from '../EFTypography'
import { ALL_NS } from '~/i18n/namespaces'
import { useTranslation } from 'react-i18next'
import { Stack } from '@mui/material'
import { DropZoneProps } from './DropZone.model'
import { ConfirmationPopup } from '../popups/ConfirmationPopup'

const baseStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  outline: 'none',
  width: '100%',
  margin: 'auto',
  // transition: 'border .24s ease-in-out',
  borderWidth: 2,
  borderStyle: 'none'
}

const focusedStyle: React.CSSProperties = {
  borderColor: '#eeeeee',
  borderRadius: 15,
  borderStyle: 'dashed',
  color: '#bdbdbd'
}

const MAX_SIZE = 10000 * 1024 * 1024 // max file size is 200MB

export function Dropzone({ children, fileTypes, multiple, onFileUpload }: DropZoneProps) {
  const { t } = useTranslation(ALL_NS)
  const acceptFileKeys = fileTypes.map(extension => `application/${extension}`)
  const { showModal } = useModal()
  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const acceptedFilesWithCorrectExtension: File[] = []

      acceptedFiles.forEach(file => {
        // accept both lower case and upper case of the extension file string
        const fileExtension = file.name.toLowerCase().split('.').pop()
        if (
          fileExtension &&
          (fileTypes.includes(fileExtension) ||
            fileTypes.includes(fileExtension.toUpperCase()))
        ) {
          acceptedFilesWithCorrectExtension.push(file)
        }
      })

      if (fileRejections.length > 0) {
        // Handle rejected files due to incorrect MIME type or extension
        // console.log('Rejected files:', fileRejections)
        showModal(ConfirmationPopup, {
          title: 'Error',
          actionType: 'Close',
          children: fileRejections.map(file =>
            file.errors.map((error, index) => (
              <EFTypography key={index}>
                {t('Message_tk0_InvalidFileUploaded', {
                  0: `[ File: ${file.file.name}]`
                })}
              </EFTypography>
            ))
          ),
          onClose: () => {}
        })
      }

      if (acceptedFilesWithCorrectExtension.length > 0) {
        // Handle accepted files
        // console.log('Accepted files:', acceptedFilesWithCorrectExtension)
        if (onFileUpload) onFileUpload(acceptedFilesWithCorrectExtension)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fileTypes, onFileUpload]
  )
  const { isDragActive, getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple,
    maxSize: MAX_SIZE,
    accept: acceptFileKeys.reduce((acc, key) => {
      acc[key] = fileTypes.map(extension => `.${extension}`)
      return acc
    }, {} as Record<string, string[]>)
  })

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? focusedStyle : {})
    }),
    [isDragActive]
  )

  return (
    <Stack {...getRootProps({ style })}>
      <input {...getInputProps()} />
      <Stack width='100%'>{children}</Stack>
    </Stack>
  )
}
